import React, { useState, useEffect, useRef, useMemo } from "react";
import ReactDOM from "react-dom";
import { useSelector } from "react-redux";
import { getAccountType } from "../store/auth/selectors";
import { checkPermission } from "../shared/utils/permissions";
import { PERMISSION_TYPES } from "../shared/utils/permissions";
import MapInviteCodePopup from "../shared/components/Popup/MapInviteCodePopup";
import useInviteCodeQueries from "../store/InviteCodes/inviteCodeQueries";
import Pagination from "../shared/components/Pagination/Pagination";
import loader from "../assets/gifs/loader.gif";
import search_icon from "../assets/images/search_icon.svg";
import closeIcon from "../assets/images/close_icon.svg";
import "./InviteCodes.css";
import useRipple from "../hooks/useRipple";
import "../hooks/useRipple.css";

const NotificationPortal = ({ children }) => {
  return ReactDOM.createPortal(children, document.body);
};

const InviteCodes = () => {
  const accountType = useSelector(getAccountType);
  const mapInviteCodeBtnRef = useRef(null);
  useRipple(mapInviteCodeBtnRef);
  const checkUserPermission = (permissionType) =>
    checkPermission(accountType, permissionType);

  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("UNUSED");
  const [showNotification, setShowNotification] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [notificationData, setNotificationData] = useState({
    code: "",
    tenant: "",
  });
  const statusSelectId = useMemo(
    () => `ic-status-${Math.random().toString(36).slice(2)}`,
    []
  );
  const searchInputId = useMemo(
    () => `ic-search-${Math.random().toString(36).slice(2)}`,
    []
  );

  useEffect(() => {
    let timer;
    if (showNotification) {
      timer = setTimeout(() => {
        setShowNotification(false);
      }, 30000);
    }
    return () => clearTimeout(timer);
  }, [showNotification]);

  useEffect(() => {
    let timer;
    if (showErrorNotification) {
      timer = setTimeout(() => {
        setShowErrorNotification(false);
      }, 30000);
    }
    return () => clearTimeout(timer);
  }, [showErrorNotification]);

  const {
    inviteCodes,
    totalCount,
    currentPage,
    itemsPerPage,
    handlePageChange,
    handleLimitChange,
    handleSearchQueryChange,
    handleStatusChange,
    isLoading,
    error,
  } = useInviteCodeQueries(1, 10, statusFilter);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      handleSearchQueryChange(searchQuery);
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  const handleShowNotification = (code, tenant) => {
    setNotificationData({ code, tenant });
    setShowNotification(true);
  };

  const handleShowErrorNotification = () => {
    setShowErrorNotification(true);
  };

  const handleCloseNotification = () => {
    setShowNotification(false);
  };

  const handleCloseErrorNotification = () => {
    setShowErrorNotification(false);
  };

  const handleCreateInviteCode = () => {
    if (checkUserPermission(PERMISSION_TYPES.CREATE_INVITE_CODES)) {
      setIsPopupVisible(true);
      setShowNotification(false);
      setShowErrorNotification(false);
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleStatusFilterChange = (e) => {
    const value = e.target.value;
    setStatusFilter(value);
    handleStatusChange(value);
  };

  const canCreateInviteCodes = checkUserPermission(
    PERMISSION_TYPES.CREATE_INVITE_CODES
  );

  return (
    <>
      <div className="ic-page">
        <div className="ic-header">
          <div className="ic-title">
            <h3 className="ic-title-heading">Invite Codes</h3>
            <p className="ic-title-subheading">Here are some of the updates.</p>
          </div>
          {canCreateInviteCodes && (
            <div className="ic-actions">
              <button
                ref={mapInviteCodeBtnRef}
                onClick={handleCreateInviteCode}
                className="dark_btn ic-map-btn"
              >
                MAP INVITE CODE
              </button>
            </div>
          )}
        </div>

        <div className="ic-filters">
          <div className="ic-search">
            <img src={search_icon} alt="Search" className="ic-search-icon" />
            <label className="ic-visually-hidden" htmlFor={searchInputId}>
              Search invite codes by tenant, code, or school
            </label>
            <input
              id={searchInputId}
              type="text"
              placeholder="Search by tenant, code or school"
              value={searchQuery}
              onChange={handleSearch}
              className="ic-search-input"
            />
          </div>
          <div className="ic-status">
            <label className="ic-label" htmlFor={statusSelectId}>
              Status
            </label>
            <select
              id={statusSelectId}
              value={statusFilter}
              onChange={handleStatusFilterChange}
              className="ic-status-select"
            >
              <option value="ALL">All</option>
              <option value="USED">Used</option>
              <option value="UNUSED">Unused</option>
            </select>
          </div>
        </div>

        <div className="ic-table">
          <div className="ic-table-header">
            <span className="ic-total-count">Total count: {totalCount}</span>
            <Pagination
              className="ic-pagination"
              totalItems={totalCount}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              itemsPerPageOptions={[10, 30, 50, 100]}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleLimitChange}
            />
          </div>

          <div className="ic-table-container">
            {isLoading ? (
              <div className="ic-loader" role="status" aria-live="polite">
                <img src={loader} alt="Loading" />
              </div>
            ) : error ? (
              <div className="ic-message ic-message--error" role="alert">
                Error: {error.message}
              </div>
            ) : inviteCodes && inviteCodes.length > 0 ? (
              <div className="ic-table-scroll">
                <table className="ic-table-el">
                  <thead>
                    <tr>
                      <th scope="col">Tenant Name</th>
                      <th scope="col">Invitation Code</th>
                      <th scope="col">School Name</th>
                      <th scope="col">School Address</th>
                      <th scope="col">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inviteCodes.map((item, index) => (
                      <tr key={`${item.invitationCode}-${index}`}>
                        <td data-label="Tenant Name">{item.tenantName}</td>
                        <td data-label="Invitation Code">
                          {item.invitationCode}
                        </td>
                        <td data-label="School Name">{item.schoolName}</td>
                        <td data-label="School Address">
                          {item.schoolAddress}
                        </td>
                        <td data-label="Status">
                          <span
                            className={`ic-status-badge ${
                              item.status === "USED"
                                ? "ic-status--used"
                                : "ic-status--unused"
                            }`}
                          >
                            {item.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="ic-message ic-message--empty" role="status">
                No results found
              </div>
            )}
          </div>
        </div>
      </div>

      <MapInviteCodePopup
        isVisible={isPopupVisible}
        onClose={() => setIsPopupVisible(false)}
        onSuccess={handleShowNotification}
        onError={handleShowErrorNotification}
      />

      {showNotification && (
        <NotificationPortal>
          <div
            className="ic-toast ic-toast--success"
            role="status"
            aria-live="polite"
          >
            <button
              type="button"
              className="ic-toast-close"
              onClick={handleCloseNotification}
              aria-label="Dismiss success message"
            >
              <img src={closeIcon} alt="" aria-hidden="true" />
            </button>
            <h3 className="ic-toast-title">Invite Code Mapped</h3>
            <p className="ic-toast-text">
              The code {notificationData.code} has been mapped to{" "}
              {notificationData.tenant}
            </p>
          </div>
        </NotificationPortal>
      )}

      {showErrorNotification && (
        <NotificationPortal>
          <div
            className="ic-toast ic-toast--error"
            role="alert"
            aria-live="assertive"
          >
            <button
              type="button"
              className="ic-toast-close"
              onClick={handleCloseErrorNotification}
              aria-label="Dismiss error message"
            >
              <img src={closeIcon} alt="" aria-hidden="true" />
            </button>
            <h3 className="ic-toast-title">Error</h3>
            <p className="ic-toast-text">
              Event could not be mapped, please try again later
            </p>
          </div>
        </NotificationPortal>
      )}
    </>
  );
};

export default InviteCodes;
